import React, { useState, useCallback } from 'react';
import * as S from './styles';
import { ProductDetails, ComparisonData } from './types';
import ImageV2 from 'shared/components/ImageV2';

interface ComparisonResultProps {
  productDetails: ProductDetails[];
  comparisonData: ComparisonData;
  isMobile: boolean;
  onClose: () => void;
  onBack: () => void;
  onAddToBag?: (productId: string) => void;
}

const ComparisonResult: React.FC<ComparisonResultProps> = ({
  productDetails,
  comparisonData,
  isMobile,
  onClose,
  onBack,
  onAddToBag,
}) => {
  const productCount = productDetails.length;

  // Track expanded/collapsed state per category
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    comparisonData.categories.forEach((cat) => {
      initial[cat.category] = cat.isExpanded !== false;
    });
    return initial;
  });

  // Toggle to only show rows with differences
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(false);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  }, []);

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ComparisonModal onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.ComparisonHeader>
          <S.BackButton onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </S.BackButton>
          <h2>Product Comparison</h2>
          <S.CloseButton onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </S.CloseButton>
        </S.ComparisonHeader>

        <S.ComparisonContent>
          {/* Product Cards */}
          <S.ProductCardsGrid isMobile={isMobile} productCount={productCount}>
            {productDetails.map((product, index) => (
              <S.DetailedProductCard
                key={index}
                productCount={productCount}
                isSticky={isMobile && index === 0}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <ImageV2
                    src={product.image}
                    alt={product.title}
                    width={180}
                    aspectRatio={0.75}
                    lazy={true}
                  />
                </div>

                <S.ProductCardInfo>
                  <S.ProductCardTitle>{product.title}</S.ProductCardTitle>
                  <S.ProductCardBrand>{product.brand}</S.ProductCardBrand>

                  <S.PriceSection>
                    <S.CurrentPrice>{product.discountedPrice || product.price}</S.CurrentPrice>
                    {product.discount && (
                      <>
                        <S.OriginalPrice>{product.price}</S.OriginalPrice>
                        <S.DiscountBadge>{product.discount}</S.DiscountBadge>
                      </>
                    )}
                  </S.PriceSection>

                  {product.starRating > 0 && (
                    <S.RatingSection>
                      <S.RatingBadge>{product.starRating.toFixed(1)} ⭐</S.RatingBadge>
                      <span>({product.reviewCount} reviews)</span>
                    </S.RatingSection>
                  )}

                  <S.QuickInfoChips>
                    {product.isFreeDelivery && (
                      <S.InfoChip variant="positive">Free Delivery</S.InfoChip>
                    )}
                    {product.codAvailable && <S.InfoChip variant="info">COD</S.InfoChip>}
                    {product.color !== 'N/A' && <S.InfoChip>{product.color}</S.InfoChip>}
                    {product.material !== 'N/A' && <S.InfoChip>{product.material}</S.InfoChip>}
                  </S.QuickInfoChips>

                  {/* <S.ViewDetailsButton>View Product Details</S.ViewDetailsButton> */}

                  <S.AddToBag onClick={() => onAddToBag?.(product.productId)}>
                    Add to Bag
                  </S.AddToBag>
                </S.ProductCardInfo>
              </S.DetailedProductCard>
            ))}
          </S.ProductCardsGrid>

          {/* Differences Toggle */}
          {comparisonData.categories.length > 0 && (
            <S.FilterBar>
              <S.SectionTitle>Detailed Comparison</S.SectionTitle>
              <S.ToggleButton
                active={showDifferencesOnly}
                onClick={() => setShowDifferencesOnly((prev) => !prev)}
              >
                {showDifferencesOnly ? 'Show All' : 'Differences Only'}
              </S.ToggleButton>
            </S.FilterBar>
          )}

          {/* Category-based Comparison Table */}
          {comparisonData.categories.map((category) => {
            const features = showDifferencesOnly
              ? category.features.filter((f) => f.isDifferent)
              : category.features;

            if (features.length === 0) return null;

            const isExpanded = expandedCategories[category.category] !== false;

            return (
              <S.CategorySection key={category.category}>
                <S.CategoryHeader onClick={() => toggleCategory(category.category)}>
                  <S.CategoryTitle>{category.category}</S.CategoryTitle>
                  <S.CategoryCount>{features.length} attributes</S.CategoryCount>
                  <S.ChevronIcon isExpanded={isExpanded}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </S.ChevronIcon>
                </S.CategoryHeader>

                {isExpanded && (
                  <S.ComparisonTable>
                    {/* Table Header */}
                    <S.ComparisonRow isMobile={isMobile} isHeader productCount={productCount}>
                      <S.FeatureLabel isHeader>Feature</S.FeatureLabel>
                      {productDetails.map((product, idx) => (
                        <S.FeatureValue key={idx} isHeader>
                          {product.title.slice(0, 30)}...
                        </S.FeatureValue>
                      ))}
                    </S.ComparisonRow>

                    {features.map((feature, index) => (
                      <S.ComparisonRow
                        key={index}
                        isMobile={isMobile}
                        productCount={productCount}
                        isDifferent={feature.isDifferent}
                      >
                        <S.FeatureLabel isDifferent={feature.isDifferent}>
                          {feature.feature}
                        </S.FeatureLabel>
                        {feature.values.map((value, valueIndex) => (
                          <S.FeatureValue
                            key={valueIndex}
                            isDifferent={feature.isDifferent}
                            dangerouslySetInnerHTML={{ __html: value }}
                          />
                        ))}
                      </S.ComparisonRow>
                    ))}
                  </S.ComparisonTable>
                )}
              </S.CategorySection>
            );
          })}
        </S.ComparisonContent>
      </S.ComparisonModal>
    </S.ModalOverlay>
  );
};

export default ComparisonResult;
